import { Button, Flex } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import React, { useMemo } from 'react';
import { useParams } from 'react-router-dom';
import CashflowContent from 'src/cashflows/CashflowContent';
import { useCurrentClient } from 'src/clients/context/CurrentClientProvider';
import FileUploadPage, {
  FileUploadTabNames,
} from 'src/data-capture/file-management/FileUploadPage';
import { FinancialContent } from 'src/financial/financial-data/FinancialContent';
import NonFinancialDataContent from 'src/non-financial-data/NonFanancialDataContent';
import UnderlineTabs from 'src/UI/page-nav/UnderlineTabs';
import TransferClientModal from 'src/home/TransferClientModal';
import useCurrentUser from 'src/admin/users/hooks/useCurrentUser';

export const ClientTabs = {
  NonFinancialData: 'non-financial-data',
  FinancialData: 'financial-data',
  Cashflows: 'cashflows',
  Documents: 'document',
} as const;
type TabName = (typeof ClientTabs)[keyof typeof ClientTabs];

export default function ClientData() {
  const { clientId, client } = useCurrentClient();
  const { tab } = useParams<{ tab: TabName }>();
  const [opened, { open, close }] = useDisclosure(false);
  const { currentUser } = useCurrentUser();

  const tabContent = useMemo(() => {
    if (tab === ClientTabs.NonFinancialData) {
      return <NonFinancialDataContent />;
    }
    if (tab === ClientTabs.FinancialData) {
      return <FinancialContent />;
    }
    if (tab === ClientTabs.Cashflows) {
      return <CashflowContent />;
    }
    if (tab === ClientTabs.Documents) {
      return <FileUploadPage />;
    }
    return null;
  }, [tab]);

  return (
    <Flex direction="column" gap="md" pos={'relative'}>
      <TransferClientModal
        currentAdvisorId={client?.advisorUserId}
        clientId={clientId}
        opened={opened}
        close={close}
      />
      <Flex direction="row" justify={'space-between'}>
        <UnderlineTabs
          mode="router"
          showClientSearch
          tabs={[
            {
              label: 'Profile',
              href: `/client/${clientId}/${ClientTabs.NonFinancialData}`,
            },
            {
              label: 'Financial',
              href: `/client/${clientId}/${ClientTabs.FinancialData}`,
            },
            {
              label: 'Cashflows',
              href: `/client/${clientId}/${ClientTabs.Cashflows}`,
            },
            {
              label: 'Documents',
              href: `/client/${clientId}/${ClientTabs.Documents}/${FileUploadTabNames.ToReview}`,
              matchHref: `/client/${clientId}/${ClientTabs.Documents}`,
            },
          ]}
        />
        {currentUser && currentUser.userRole !== 'ADVISOR' && (
          <Button variant="default" onClick={open}>
            Transfer client
          </Button>
        )}
      </Flex>

      {tabContent}
    </Flex>
  );
}
